import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import type { AccountItem, PaymentStatus, AccountingFilter, AccountingStats } from "@/types/accounting";
import { useUser } from "@/context/UserContext";

const paymentStatusTabs: Array<{ value: PaymentStatus | "all"; label: string }> = [
  { value: "all", label: "全部" },
  { value: "未付款", label: "未付款" },
  { value: "已通知", label: "已通知" },
  { value: "已付款", label: "已付款" },
];

const PAGE_SIZE = 10;

const ASSIGNEES = ["未指派", "陳建宏", "林雅芳", "王志明", "李曉萍", "張俊凱", "劉美鳳"];

const ASSIGNEE_QUOTAS: Record<string, number> = {
  "陳建宏": 400000,
  "林雅芳": 400000,
  "王志明": 400000,
  "李曉萍": 400000,
  "張俊凱": 300000,
  "劉美鳳": 300000,
};

interface EmployeeOption {
  id: string;
  name: string;
  email: string;
}

function getPaymentStatusColor(status: PaymentStatus): string {
  switch (status) {
    case "未付款": return "bg-accent-100 text-accent-700";
    case "已通知": return "bg-secondary-100 text-secondary-700";
    case "已付款": return "bg-primary-100 text-primary-700";
    default: return "bg-background-100 text-foreground-600";
  }
}

function mapRowToItem(row: Record<string, unknown>): AccountItem {
  return {
    id: row.id as string,
    orderNumber: row.order_number as string,
    company: row.company as string,
    product: row.product as string,
    quantity: (row.quantity as number) || 1,
    unit: (row.unit as string) || "瓶",
    amount: Number(row.amount) || 0,
    currency: (row.currency as string) || "NT$",
    dueDate: row.due_date as string,
    transferTime: row.transfer_time as string,
    paymentStatus: row.payment_status as PaymentStatus,
    assignee: (row.assignee as string) || "未指派",
    status: (row.status as string) || "處理中",
    note: (row.note as string) || "",
    employeeId: row.employee_id as string,
    createdAt: row.created_at as string,
    updatedAt: (row.updated_at as string) || (row.created_at as string),
  };
}

export default function AccountingPage() {
  const user = useUser();
  const [filter, setFilter] = useState<AccountingFilter>({
    search: "",
    paymentStatus: "all",
    assignee: "all",
    employeeId: user.isAdmin ? "all" : (user.employeeId || ""),
  });
  const [items, setItems] = useState<AccountItem[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [submittingTrial, setSubmittingTrial] = useState(false);
  const hasAutoGeneratedRef = useRef(false);

  const showToast = useCallback((message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Auto-generate orders if fewer than 20 records exist
  useEffect(() => {
    if (hasAutoGeneratedRef.current) return;
    if (loading) return;
    if (items.length >= 20) {
      hasAutoGeneratedRef.current = true;
      return;
    }

    const generateMissing = async () => {
      hasAutoGeneratedRef.current = true;

      // Determine which employee_id to generate orders for
      let targetEmployeeId = filter.employeeId;
      if (targetEmployeeId === "all" || !targetEmployeeId) {
        // Admin viewing all: use own employee_id if available, otherwise first employee
        targetEmployeeId = user.employeeId || employees[0]?.id || "";
      }
      if (!targetEmployeeId) {
        showToast("無法自動生成：找不到對應的員工 ID", "error");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("generate-accounting-orders", {
          body: { employee_id: targetEmployeeId },
        });

        if (error) {
          showToast(`自動生成失敗：${error.message}`, "error");
          return;
        }

        if (data?.success && data.inserted > 0) {
          showToast(`已自動生成 ${data.inserted} 筆帳務訂單`);
        }

        // Re-fetch to include newly inserted orders
        const { data: freshData } = await supabase
          .from("accounting_items")
          .select("*")
          .order("created_at", { ascending: false });
        if (freshData) {
          setItems((freshData as unknown[]).map((row) => mapRowToItem(row as Record<string, unknown>)));
        }
      } catch (err) {
        showToast(`自動生成失敗：${(err as Error).message}`, "error");
      }
    };

    generateMissing();
  }, [items.length, loading, filter.employeeId, user.employeeId, employees, showToast]);

  // Fetch employees list
  useEffect(() => {
    supabase
      .from("employees")
      .select("id, name, email")
      .order("name")
      .then(({ data, error }) => {
        if (!error && data) {
          setEmployees(data as EmployeeOption[]);
        }
      });
  }, []);

  // Fetch accounting items
  const fetchItems = useCallback(() => {
    setLoading(true);
    setLoadError("");
    supabase
      .from("accounting_items")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          setLoadError(error.message);
          setItems([]);
        } else if (data) {
          setItems((data as unknown[]).map((row) => mapRowToItem(row as Record<string, unknown>)));
        }
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const availableEmployees = useMemo(() => {
    if (user.isAdmin) return employees;
    return employees.filter((e) => e.id === user.employeeId);
  }, [user.isAdmin, user.employeeId, employees]);

  // Today's date string for daily quota tracking — MUST be declared before filteredItems which uses it
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Today assigned items list for the cards above quota overview
  const todayAssignedItems = useMemo(() => {
    const scopeItems = filter.employeeId === "all" ? items : items.filter((i) => i.employeeId === filter.employeeId);
    return scopeItems.filter((i) => i.assignee !== "未指派" && i.paymentStatus !== "已付款");
  }, [items, filter.employeeId]);

  const filteredItems = useMemo(() => {
    let result = [...items];
    if (filter.employeeId !== "all") {
      result = result.filter((i) => i.employeeId === filter.employeeId);
    }
    if (filter.paymentStatus !== "all") {
      result = result.filter((i) => i.paymentStatus === filter.paymentStatus);
    }
    if (filter.assignee !== "all") {
      result = result.filter((i) => i.assignee === filter.assignee);
    }
    if (filter.search.trim()) {
      const term = filter.search.toLowerCase();
      result = result.filter(
        (i) =>
          i.orderNumber.toLowerCase().includes(term) ||
          i.company.toLowerCase().includes(term) ||
          i.product.toLowerCase().includes(term)
      );
    }
    // Exclude assigned-but-not-paid items: they only show in the quota overview section
    result = result.filter((i) => !(i.assignee !== "未指派" && i.paymentStatus !== "已付款"));
    // Sort: 已付款 at the bottom, rest by created_at desc
    result.sort((a, b) => {
      if (a.paymentStatus === "已付款" && b.paymentStatus !== "已付款") return 1;
      if (a.paymentStatus !== "已付款" && b.paymentStatus === "已付款") return -1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return result;
  }, [items, filter]);

  const stats: AccountingStats = useMemo(() => {
    const relevant = filter.employeeId === "all" ? items : items.filter((i) => i.employeeId === filter.employeeId);
    const unpaid = relevant.filter((i) => i.paymentStatus === "未付款");
    const notified = relevant.filter((i) => i.paymentStatus === "已通知");
    const paid = relevant.filter((i) => i.paymentStatus === "已付款");
    const totalAmount = relevant.reduce((sum, i) => sum + i.amount, 0);
    const paidAmount = paid.reduce((sum, i) => sum + i.amount, 0);
    const collectionRate = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;
    return {
      totalAmount,
      totalCount: relevant.length,
      unpaidAmount: unpaid.reduce((sum, i) => sum + i.amount, 0),
      unpaidCount: unpaid.length,
      notifiedAmount: notified.reduce((sum, i) => sum + i.amount, 0),
      notifiedCount: notified.length,
      paidAmount,
      paidCount: paid.length,
      collectionRate,
    };
  }, [items, filter.employeeId]);

  // Trial calculation: per-assignee usage from today's assigned items (all payment statuses)
  const trialAssigneeData = useMemo(() => {
    // Only consider items matching the current employee filter
    const scopeItems = filter.employeeId === "all" ? items : items.filter((i) => i.employeeId === filter.employeeId);
    // Use updatedAt to track "today": assignee changes & payment status changes both update updated_at,
    // so today's edits are counted in today's quota. Previous-day items auto-clear next day.
    const activeItems = scopeItems.filter((i) => i.assignee !== "未指派" && i.updatedAt.slice(0, 10) === todayStr);

    return ASSIGNEES.map((name) => {
      const assigned = activeItems.filter((i) => i.assignee === name);
      const usedAmount = assigned.reduce((sum, i) => sum + i.amount, 0);
      const quota = ASSIGNEE_QUOTAS[name] || 0;
      const remaining = Math.max(0, quota - usedAmount);
      const usagePct = quota > 0 ? Math.min(100, Math.round((usedAmount / quota) * 100)) : 0;
      return {
        name,
        quota,
        usedAmount,
        remaining,
        usagePct,
        assignedCount: assigned.length,
      };
    });
  }, [items, filter.employeeId, todayStr]);

  // Trial items that will be submitted (currently assigned, not paid)
  const trialPendingItems = useMemo(() => {
    const scopeItems = filter.employeeId === "all" ? items : items.filter((i) => i.employeeId === filter.employeeId);
    return scopeItems.filter((i) => i.paymentStatus !== "已付款" && i.assignee !== "未指派");
  }, [items, filter.employeeId]);

  const trialTotalAmount = useMemo(() => {
    return trialPendingItems.reduce((sum, i) => sum + i.amount, 0);
  }, [trialPendingItems]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const paginatedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter.paymentStatus, filter.search, filter.employeeId, filter.assignee]);

  const handlePaymentStatusChange = useCallback(async (itemId: string, newStatus: PaymentStatus) => {
    const newOverallStatus = newStatus === "已付款" ? "已結案" : "處理中";
    const nowStr = new Date().toISOString();
    setItems((prev) =>
      prev.map((i) =>
        i.id === itemId
          ? { ...i, paymentStatus: newStatus, status: newOverallStatus, updatedAt: nowStr }
          : i
      )
    );

    const { error } = await supabase
      .from("accounting_items")
      .update({
        payment_status: newStatus,
        status: newOverallStatus,
        updated_at: new Date().toISOString(),
      })
      .eq("id", itemId);

    if (error) {
      showToast(`更新失敗：${error.message}`, "error");
      fetchItems();
    } else {
      showToast(`付款狀態已更新為「${newStatus}」`);
    }
  }, [showToast, fetchItems]);

  const handleAssigneeChange = useCallback(async (itemId: string, newAssignee: string) => {
    const nowStr = new Date().toISOString();
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, assignee: newAssignee, updatedAt: nowStr } : i)));

    const { error } = await supabase
      .from("accounting_items")
      .update({ assignee: newAssignee, updated_at: new Date().toISOString() })
      .eq("id", itemId);

    if (error) {
      showToast(`更新失敗：${error.message}`, "error");
      fetchItems();
    }
  }, [showToast, fetchItems]);

  const handleDeleteItem = useCallback(async (itemId: string) => {
    const { error } = await supabase
      .from("accounting_items")
      .delete()
      .eq("id", itemId);

    if (error) {
      showToast(`刪除失敗：${error.message}`, "error");
      return;
    }

    setItems((prev) => prev.filter((i) => i.id !== itemId));
    showToast("帳務記錄已刪除");
  }, [showToast]);

  // Submit trial: bulk mark all assigned non-paid items as "已付款"
  const handleTrialSubmit = useCallback(async () => {
    if (trialPendingItems.length === 0) {
      showToast("目前沒有可送出的訂單（需先指派專員且尚未付款）", "error");
      return;
    }

    setSubmittingTrial(true);
    const ids = trialPendingItems.map((i) => i.id);

    const { error } = await supabase
      .from("accounting_items")
      .update({
        payment_status: "已付款",
        status: "已結案",
        updated_at: new Date().toISOString(),
      })
      .in("id", ids);

    setSubmittingTrial(false);

    if (error) {
      showToast(`送出失敗：${error.message}`, "error");
      return;
    }

    // Update local state
    const nowStr = new Date().toISOString();
    setItems((prev) =>
      prev.map((i) =>
        ids.includes(i.id)
          ? { ...i, paymentStatus: "已付款" as PaymentStatus, status: "已結案", updatedAt: nowStr }
          : i
      )
    );

    const totalAmount = trialPendingItems.reduce((sum, i) => sum + i.amount, 0);
    showToast(`送出成功！${trialPendingItems.length} 筆訂單已設為已付款，總額 ${formatCurrency(totalAmount)}`);
  }, [trialPendingItems, showToast]);

  const formatCurrency = (amount: number) => `NT$ ${amount.toLocaleString("zh-TW")}`;

  const selectedEmployeeName = filter.employeeId === "all"
    ? "全部員工"
    : employees.find((e) => e.id === filter.employeeId)?.name || "";

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
          toast.type === "error" ? "bg-accent-500 text-background-50" : "bg-foreground-800 text-background-50"
        }`}>
          <i className={`${toast.type === "error" ? "ri-error-warning-line" : "ri-check-line"} text-primary-400`} />
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
              帳務管理
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              應收帳款管理{user.isAdmin ? `，資料依員工隔離（${user.userEmail}）` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { fetchItems(); showToast("資料已刷新"); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap shrink-0"
            >
              <i className="ri-refresh-line" />
              全部刷新
            </button>
          </div>
        </div>

        {/* Employee selector */}
        {user.isAdmin && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-background-200 bg-background-50">
            <i className="ri-user-3-line text-foreground-400 text-sm" />
            <select
              value={filter.employeeId}
              onChange={(e) => setFilter((prev) => ({ ...prev, employeeId: e.target.value }))}
              className="text-sm text-foreground-900 bg-transparent border-none outline-none cursor-pointer min-w-[160px]"
            >
              {user.isAdmin && <option value="all">全部員工</option>}
              {availableEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}（{emp.email}）
                </option>
              ))}
            </select>
          </div>
          <span className="text-xs text-foreground-400">
            {selectedEmployeeName ? `目前檢視：${selectedEmployeeName}` : ""}
          </span>
          {loading && (
            <span className="text-xs text-foreground-400 flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin" />
              載入中...
            </span>
          )}
          {loadError && (
            <span className="text-xs text-accent-600 flex items-center gap-1">
              <i className="ri-error-warning-line" />
              {loadError}
              <button onClick={fetchItems} className="underline cursor-pointer ml-1">重試</button>
            </span>
          )}
        </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-500">未收款</p>
            <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-money-cny-circle-line text-accent-500 text-sm" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900 mt-2">{formatCurrency(stats.unpaidAmount)}</p>
          <p className="text-xs text-foreground-400 mt-0.5">{stats.unpaidCount} 筆</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-500">已通知帳款</p>
            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-notification-3-line text-secondary-500 text-sm" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900 mt-2">{formatCurrency(stats.notifiedAmount)}</p>
          <p className="text-xs text-foreground-400 mt-0.5">{stats.notifiedCount} 筆</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-500">已收款</p>
            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-check-double-line text-primary-500 text-sm" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900 mt-2">{formatCurrency(stats.paidAmount)}</p>
          <p className="text-xs text-foreground-400 mt-0.5">{stats.paidCount} 筆</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-500">總帳款金額</p>
            <div className="w-8 h-8 rounded-lg bg-foreground-100 flex items-center justify-center">
              <i className="ri-bill-line text-foreground-500 text-sm" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900 mt-2">{formatCurrency(stats.totalAmount)}</p>
          <p className="text-xs text-foreground-400 mt-0.5">{stats.totalCount} 筆</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-500">收款率</p>
            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-pie-chart-line text-primary-500 text-sm" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900 mt-2">{stats.collectionRate}%</p>
          <p className="text-xs text-foreground-400 mt-0.5">已付 / 總筆數</p>
        </div>
      </div>

      {/* Trial Calculation Area */}
      <div className="bg-background-50 border border-background-200 rounded-xl">
        <div className="p-4 border-b border-background-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-calculator-line text-primary-500" />
                專員額度總覽
              </h2>
              <p className="text-xs text-foreground-500 mt-0.5">
                指派專員後訂單從下方明細移至這裡，確認無誤後點送出，訂單回到下方明細並顯示為已付款。
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-foreground-500">待送出</p>
                <p className="text-sm font-bold text-foreground-900">
                  {trialPendingItems.length} 筆 / {formatCurrency(trialTotalAmount)}
                </p>
              </div>
              <button
                onClick={handleTrialSubmit}
                disabled={submittingTrial || trialPendingItems.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap shrink-0"
              >
                {submittingTrial ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    送出中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" />
                    確認送出
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Assignee quota cards */}
        <div className="p-4">
          {/* Quota cards row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {trialAssigneeData.map((ad) => {
              const isOver = ad.usedAmount > ad.quota;
              const barColor = isOver ? "bg-accent-500" : ad.usagePct >= 80 ? "bg-accent-500" : ad.usagePct >= 50 ? "bg-secondary-500" : "bg-primary-500";
              const barBg = isOver ? "bg-accent-100" : "bg-background-200";
              return (
                <div key={ad.name} className={`rounded-xl border p-3 transition-colors ${isOver ? "border-accent-300 bg-accent-50/50" : "border-background-200 bg-background-50"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-foreground-900">{ad.name}</p>
                    {ad.assignedCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500">
                        {ad.assignedCount} 筆
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-[11px] text-foreground-500">已用</span>
                    <span className={`text-sm font-bold ${isOver ? "text-accent-600" : "text-foreground-900"}`}>
                      {formatCurrency(ad.usedAmount)}
                    </span>
                  </div>
                  <div className={`w-full h-2 rounded-full ${barBg} mb-2 overflow-hidden`}>
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${barColor}`}
                      style={{ width: `${Math.min(100, ad.usagePct)}%` }}
                    />
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] text-foreground-500">剩餘額度</span>
                    <span className={`text-xs font-bold ${ad.remaining <= 0 ? "text-accent-600" : "text-primary-600"}`}>
                      {formatCurrency(ad.remaining)}
                    </span>
                  </div>
                  <p className="text-[10px] text-foreground-400 mt-0.5 text-right">每日額度 {formatCurrency(ad.quota)}</p>
                </div>
              );
            })}
          </div>

          {/* Quota legend */}
          <div className="flex items-center gap-4 mt-3 text-[11px] text-foreground-500">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-primary-500" />
              安全 (&lt; 50%)
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-secondary-500" />
              注意 (50% ~ 80%)
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-accent-500" />
              超標 (&gt; 80%)
            </div>
          </div>

          {/* Today assigned orders list */}
          {todayAssignedItems.length > 0 && (
            <div className="mt-4 border-t border-background-200 pt-4">
              <p className="text-xs font-bold text-foreground-700 mb-2">今日已指派訂單</p>
              <div className="flex flex-col gap-1.5">
                {todayAssignedItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-background-50 border border-background-200 rounded-lg px-3 py-2 flex items-center gap-3"
                  >
                    <span className="text-[10px] font-mono text-foreground-600 w-28 shrink-0 truncate">{item.orderNumber}</span>
                    <p className="text-xs text-foreground-900 truncate flex-1 min-w-0">{item.company}</p>
                    <p className="text-[11px] text-foreground-500 truncate flex-1 min-w-0">{item.product} × {item.quantity}{item.unit}</p>
                    <span className="text-xs font-bold text-foreground-900 w-20 text-right shrink-0">{formatCurrency(item.amount)}</span>
                    <select
                      value={item.paymentStatus}
                      onChange={(e) => handlePaymentStatusChange(item.id, e.target.value as PaymentStatus)}
                      className={`text-[10px] px-1 py-0.5 rounded w-14 text-center shrink-0 outline-none cursor-pointer border-none ${getPaymentStatusColor(item.paymentStatus)}`}
                    >
                      <option value="未付款">未付款</option>
                      <option value="已通知">已通知</option>
                      <option value="已付款">已付款</option>
                    </select>
                    <select
                      value={item.assignee}
                      onChange={(e) => handleAssigneeChange(item.id, e.target.value)}
                      className="text-[10px] px-1 py-0.5 rounded border border-background-200 bg-background-50 outline-none cursor-pointer w-16 text-center shrink-0"
                    >
                      {ASSIGNEES.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    <span className="text-[10px] text-foreground-400 w-24 text-right shrink-0">到期 {item.dueDate}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Section */}
      <div className="bg-background-50 border border-background-200 rounded-xl">
        <div className="p-4 border-b border-background-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-file-list-3-line text-primary-500" />
                帳務明細
              </h2>
              <p className="text-xs text-foreground-500 mt-0.5">
                已指派專員的訂單顯示在上方額度總覽區，確認送出後回到此處
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={filter.assignee}
                onChange={(e) => setFilter((prev) => ({ ...prev, assignee: e.target.value }))}
                className="text-xs px-3 py-2 rounded-lg border border-background-200 bg-background-50 outline-none cursor-pointer"
              >
                <option value="all">全部專員</option>
                {ASSIGNEES.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <div className="relative w-48">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                <input
                  type="text"
                  value={filter.search}
                  onChange={(e) => setFilter((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="搜尋 訂單 / 公司 / 商品"
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
            </div>
          </div>

          {/* Payment Status Tabs */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {paymentStatusTabs.map((tab) => {
              const count = tab.value === "all"
                ? filteredItems.length
                : items.filter((i) => {
                    if (filter.employeeId !== "all" && i.employeeId !== filter.employeeId) return false;
                    return i.paymentStatus === tab.value;
                  }).length;
              return (
                <button
                  key={tab.value}
                  onClick={() => setFilter((prev) => ({ ...prev, paymentStatus: tab.value }))}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                    filter.paymentStatus === tab.value
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {tab.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    filter.paymentStatus === tab.value ? "bg-background-50/20 text-background-50" : "bg-background-200 text-foreground-500"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-50">
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-32">關聯訂單</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">公司</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">商品</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 w-24">金額</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-28">到期日</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-28">轉入時間</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-24">付款狀態</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-24">負責專員</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-20">狀態</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 w-20">操作</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((item) => (
                <tr key={item.id} className="border-b border-background-100 hover:bg-background-50/60 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-foreground-700">{item.orderNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground-900">{item.company}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground-700">{item.product}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{item.quantity}{item.unit}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-foreground-900 whitespace-nowrap">
                    {formatCurrency(item.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground-600">
                    <div className="flex items-center gap-1">
                      <span>{item.dueDate}</span>
                      <i className="ri-calendar-line text-foreground-400 text-xs" />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground-500">{item.transferTime}</td>
                  <td className="px-4 py-3">
                    <select
                      value={item.paymentStatus}
                      onChange={(e) => handlePaymentStatusChange(item.id, e.target.value as PaymentStatus)}
                      className={`text-xs font-medium px-2 py-1 rounded-md border-none outline-none cursor-pointer ${getPaymentStatusColor(item.paymentStatus)}`}
                    >
                      <option value="未付款">未付款</option>
                      <option value="已通知">已通知</option>
                      <option value="已付款">已付款</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={item.assignee}
                      onChange={(e) => handleAssigneeChange(item.id, e.target.value)}
                      className="text-xs px-2 py-1 rounded border border-background-200 bg-background-50 outline-none cursor-pointer"
                    >
                      <option value="未指派">未指派</option>
                      {ASSIGNEES.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${item.status === "已結案" ? "bg-primary-100 text-primary-700" : "bg-secondary-100 text-secondary-700"}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-accent-100 text-accent-600 hover:bg-accent-200 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line" />
                      刪除
                    </button>
                  </td>
                </tr>
              ))}
              {paginatedItems.length === 0 && !loading && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-foreground-400">
                    <i className="ri-inbox-line text-2xl mb-2 block" />
                    找不到符合條件的帳務紀錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-background-200">
          <p className="text-xs text-foreground-500">
            共 {filteredItems.length} 筆，第 {currentPage} / {totalPages} 頁
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" /> Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page: number;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-7 h-7 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                    currentPage === page
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {page}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <span className="text-xs text-foreground-400 px-1">...</span>
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Next <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}